<!-- eslint-disable vue/no-mutating-props -->
<!-- 门店列表与营业设置的统计、筛选和卡片展示。门店操作和弹窗由父页面处理。 -->
<template>
  <section class="store-panel">
    <div class="summary-grid">
      <article v-for="stat in stats" :key="stat.label" class="summary-card">
        <div>
          <span>{{ stat.label }}</span>
          <strong>{{ stat.value }}</strong>
        </div>
        <small :class="`is-${stat.tone ?? 'brand'}`">{{ stat.sub }}</small>
      </article>
    </div>

    <article class="content-card">
      <div class="filter-bar">
        <el-select v-model="filters.keywords" multiple filterable placeholder="全部门店" class="filter-control">
          <el-option
            v-for="store in stores"
            :key="store.id"
            :label="store.code ? `${store.name}（${store.code}）` : store.name"
            :value="store.code || store.name"
          />
        </el-select>
        <div class="filter-row">
          <el-select v-model="filters.status" clearable placeholder="营业状态" class="filter-control">
            <el-option v-for="option in storeStatusOptions" :key="option.value" :label="option.label" :value="option.value" />
          </el-select>
        </div>
        <div class="filter-bar__actions">
          <button class="search-button" @click="emit('search')">搜索</button>
          <button class="reset-button" @click="emit('reset')">重置</button>
        </div>
      </div>

      <div class="card-grid">
        <article v-for="store in stores" :key="store.id" class="business-card">
          <div class="business-card__head">
            <div>
              <h2>{{ store.name }}</h2>
              <small>门店编码: {{ store.code }}</small>
            </div>
            <span :class="['status-tag', toneClass(storeStatusTone(store.businessStatus))]">
              {{ getStoreStatusText(store.businessStatus) }}
            </span>
          </div>
          <div class="business-card__body">
            <p>{{ store.address || '暂无地址' }}</p>
            <p>{{ store.phone || '暂无联系电话' }}</p>
            <p>门店类别 {{ formatStoreCategories(store) }}</p>
            <p>营业时间 {{ store.openTime || '--:--' }} - {{ store.closeTime || '--:--' }}</p>
            <p>配送 {{ store.supportsDelivery ? '支持' : '不支持' }} / 无人机 {{ store.supportsDrone ? '支持' : '不支持' }}</p>
          </div>
          <div class="business-card__footer">
            <span>创建于 {{ store.createdAt || '--' }}</span>
            <div>
              <button @click="emit('viewStats', store)">统计</button>
              <button @click="emit('editStore', store)">编辑</button>
              <button :disabled="deletingStoreId === store.id" @click="emit('removeStore', store)">
                {{ deletingStoreId === store.id ? '删除中' : '删除' }}
              </button>
            </div>
          </div>
        </article>
        <el-empty v-if="!stores.length && !loading" description="暂无门店数据" />
      </div>
    </article>
  </section>
</template>

<script setup lang="ts">
import { toRefs } from 'vue'
import type { AdminStore } from '@/api/store'
import type { OperationStat } from '@/data/operationDesign'

type StatusTone = 'brand' | 'success' | 'warning' | 'error' | 'muted'

interface StoreFilters {
  keywords: string[]
  status: string
}

interface StoreStatusOption {
  value: string
  label: string
}

const props = defineProps<{
  stats: OperationStat[]
  stores: AdminStore[]
  filters: StoreFilters
  storeStatusOptions: StoreStatusOption[]
  loading: boolean
  deletingStoreId: string
  getStoreStatusText: (status: string) => string
  storeStatusTone: (status: string) => StatusTone
  toneClass: (tone: StatusTone) => string
}>()

const emit = defineEmits<{
  search: []
  reset: []
  viewStats: [store: AdminStore]
  editStore: [store: AdminStore]
  removeStore: [store: AdminStore]
}>()

const {
  stats,
  stores,
  filters,
  storeStatusOptions,
  loading,
  deletingStoreId,
  getStoreStatusText,
  storeStatusTone,
  toneClass,
} = toRefs(props)

function formatStoreCategories(store: AdminStore): string {
  if (store.categories?.length) return store.categories.join('、')
  return store.category || '全部类别'
}
</script>

<style scoped lang="scss">
.store-panel { display: flex; flex-direction: column; gap: 16px; }
.summary-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 16px; }
.summary-card { min-height: 102px; padding: 20px; border: 1px solid var(--jf-color-border); border-radius: 12px; background: #fff; display: flex; align-items: center; justify-content: space-between; box-shadow: 0 2px 10px rgba(37, 40, 43, .04); }
.summary-card div { min-width: 0; }
.summary-card span { display: block; margin-bottom: 8px; color: var(--jf-color-text-secondary); font-size: 14px; }
.summary-card strong { color: var(--jf-color-text-primary); font-size: 26px; line-height: 1.1; word-break: break-word; }
.summary-card small { padding: 4px 8px; border-radius: 6px; background: var(--jf-color-background); font-size: 12px; white-space: nowrap; }
.content-card { border: 1px solid var(--jf-color-border); border-radius: 12px; background: #fff; box-shadow: 0 2px 10px rgba(37, 40, 43, .04); overflow: hidden; }
.filter-bar { min-height: 66px; padding: 14px 16px; border-bottom: 1px solid var(--jf-color-border); display: flex; align-items: center; flex-wrap: wrap; gap: 12px; }
.filter-control { width: 220px; }
.filter-row { display: flex; gap: 12px; }
.filter-row .filter-control { min-width: 124px; }
.filter-bar__actions { display: flex; gap: 8px; margin-left: auto; }
.filter-row { display: flex; gap: 12px; }
.filter-row .filter-select { min-width: 124px; }
button { border: 0; cursor: pointer; font: inherit; }
.search-button, .reset-button { height: 36px; padding: 0 16px; border-radius: 8px; font-size: 14px; font-weight: 700; }
.search-button { background: var(--jf-color-primary); color: #fff; }
.reset-button { border: 1px solid var(--jf-color-border); background: #fff; color: var(--jf-color-text-primary); }
.card-grid { padding: 16px; display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; }
.business-card { padding: 16px; border: 1px solid var(--jf-color-border); border-radius: 12px; transition: border-color .2s, box-shadow .2s; }
.business-card:hover { border-color: rgba(118, 183, 232, .55); box-shadow: 0 8px 22px rgba(37, 40, 43, .06); }
.business-card__head, .business-card__footer { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; }
.business-card h2 { margin: 0; color: var(--jf-color-text-primary); font-size: 18px; }
.business-card small { display: block; margin-top: 6px; color: var(--jf-color-text-secondary); font-size: 12px; }
.business-card__body { margin: 16px 0; display: grid; gap: 8px; color: var(--jf-color-text-secondary); font-size: 14px; }
.business-card__body p { margin: 0; }
.business-card__footer { padding-top: 14px; border-top: 1px solid var(--jf-color-border); align-items: center; color: var(--jf-color-text-secondary); font-size: 13px; }
.business-card__footer div { display: flex; gap: 12px; }
.business-card__footer button { background: transparent; color: var(--jf-color-primary-dark); font-size: 13px; }
.status-tag { display: inline-flex; align-items: center; min-height: 24px; padding: 3px 9px; border-radius: 999px; font-size: 12px; font-weight: 700; }
.is-brand { background: rgba(118, 183, 232, .14); color: var(--jf-color-primary-dark); }
.is-success { background: rgba(69, 185, 124, .12); color: var(--jf-color-success); }
.is-warning { background: rgba(242, 169, 80, .14); color: var(--jf-color-warning); }
.is-error { background: rgba(231, 101, 101, .12); color: var(--jf-color-error); }
.is-muted { background: #f1f3f5; color: var(--jf-color-text-secondary); }
@media (max-width: 1180px) {
  .summary-grid, .card-grid { grid-template-columns: 1fr; }
}
</style>
